import React, { useCallback, useState } from "react";
import { connect } from "react-redux";

import { faExclamationTriangle } from "@fortawesome/pro-solid-svg-icons/faExclamationTriangle";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Popover, { PopoverProps } from "antd/es/popover";
import Tooltip from "antd/es/tooltip";
import cn from "classnames";
import { useIntl } from "react-intl";

import PermissionType from "@mapmycustomers/shared/enum/PermissionType";
import Address from "@mapmycustomers/shared/types/Address";
import { EntityWithLocation } from "@mapmycustomers/shared/types/entity";
import { stopEvents } from "@mapmycustomers/shared/util/browser";
import hasPermissionForEntityType from "@mapmycustomers/shared/util/hasPermissionForEntityType";
import { Button } from "@mapmycustomers/ui";

import { updateEntityAddress } from "@app/store/entityAddress/actions";
import { hasPermission } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";

import AddressWarning from "./components/AddressWarning";
import styles from "./EntityIncompleteAddressWarning.module.scss";

interface EntityIncompleteAddressWarningProps {
  autoAdjustOverflow?: PopoverProps["autoAdjustOverflow"];
  className?: string;
  entity: EntityWithLocation;
  loading?: boolean;
  placement?: PopoverProps["placement"];
}

const EntityIncompleteAddressWarning = ({
  autoAdjustOverflow = true,
  className,
  entity,
  hasPermission,
  loading,
  placement,
  updateEntityAddress,
}: EntityIncompleteAddressWarningProps &
  typeof mapDispatchToProps &
  ReturnType<typeof mapStateToProps>) => {
  const intl = useIntl();

  const [popoverVisible, setPopoverVisible] = useState<boolean>(false);
  const [tooltipVisible, setTooltipVisible] = useState<boolean>(false);

  const handleSetPopoverVisible = useCallback((visible: boolean) => {
    setPopoverVisible(visible);
    setTooltipVisible(false);
  }, []);

  const handleOk = useCallback(
    (address: Address) => {
      updateEntityAddress({
        address,
        entity,
      });

      setTooltipVisible(false);
      setPopoverVisible(false);
    },
    [entity, updateEntityAddress]
  );

  const handleCancel = useCallback(() => {
    setTooltipVisible(false);
    setPopoverVisible(false);
  }, []);

  const canEdit =
    entity.accessStatus?.update ??
    hasPermissionForEntityType(hasPermission, PermissionType.UPDATE, entity.entity);

  return (
    <span className={cn(styles.container, className)} onClick={stopEvents}>
      <Tooltip
        autoAdjustOverflow
        onOpenChange={setTooltipVisible}
        title={intl.formatMessage({
          id: "entity.incompleteAddressWarning.tooltip",
          defaultMessage:
            "Address is partially invalid and may not show on map as expected. Click for details.",
          description: "Incomplete address warning popover - tooltip",
        })}
        trigger={popoverVisible ? [] : ["hover"]}
        visible={tooltipVisible}
      >
        <Popover
          arrowPointAtCenter
          autoAdjustOverflow={autoAdjustOverflow}
          content={
            <div className={styles.content}>
              <div className={styles.title}>
                <FontAwesomeIcon className={styles.icon} icon={faExclamationTriangle} />
                {intl.formatMessage({
                  id: "entity.incompleteAddressWarning.popover.title",
                  defaultMessage: "Partially Invalid Address",
                  description: "Incomplete address warning popover - title",
                })}
              </div>

              <AddressWarning
                addressEditable={canEdit}
                entity={entity}
                loading={loading}
                onCancel={handleCancel}
                onOk={handleOk}
              />
            </div>
          }
          onVisibleChange={handleSetPopoverVisible}
          overlayClassName={styles.overlay}
          placement={placement ?? "top"}
          trigger={["click"]}
          visible={popoverVisible}
        >
          <Button
            icon={<FontAwesomeIcon className={styles.icon} icon={faExclamationTriangle} />}
            iconOnly
            size="small"
            type="link"
          />
        </Popover>
      </Tooltip>
    </span>
  );
};

const mapStateToProps = (state: RootState) => ({
  hasPermission: hasPermission(state),
});

const mapDispatchToProps = {
  updateEntityAddress,
};

export default connect(mapStateToProps, mapDispatchToProps)(EntityIncompleteAddressWarning);
